import * as rd from '@devexperts/remote-data-ts'
import { SpotType } from '@spiaggeit/spit-core'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { CartFooter } from '@/components/CartFooter'
import { LoaderTrigger } from '@/components/LoaderTrigger'
import { SelectedElementDrawer } from '@/components/SelectedElementDrawer'
import { SelectedServiceDrawer } from '@/components/SelectedServiceDrawer'
import { useAppDispatch, useAppSelector } from '@/hooks/store'
import { useFetchPricePreview } from '@/hooks/useFetchPricePreview'
import { useResetInsertInsuranceProducts } from '@/hooks/useResetInsertInsurance'
import { useSyncRestoredCartItems } from '@/hooks/useRestorePendingCart'
import { ServiceInfoPurchaseType } from '@/models/service'
import {
  BookingAvailability,
  bookingAvailabilitySlice,
} from '@/store/bookingAvailabilitySlice'
import { cartSlice } from '@/store/cartSlice'
import { store } from '@/store/configureStore'
import { extraSlice } from '@/store/extraSlice'
import { licenseSlice } from '@/store/licenseSlice'
import {
  loadMapData,
  loadSectors,
  mapSelectors,
  mapSlice,
  mappedSectorsSelector,
} from '@/store/mapSlice'
import { servicesSlice } from '@/store/servicesSlice'
import {
  buildGaContextItem,
  GTM_CURRENCY_CODE,
  pushEcommerceEvent,
} from '@/utils/analytics/ecommerceDataLayer'
import { MAP_CELL_SIZE } from '@/utils/constants'
import { getObjectsComparison } from '@/utils/getObjectsComparison'
import { computeWarehouseAdjustedMapElements } from '@/utils/map/applyWarehouseMapAvailability'
import { normalizeReservation } from '@/utils/normalizeReservation'
import { normalizeSpot } from '@/utils/normalizeSpot'

import { SectorCard } from './SectorCard'

export function SectorsRoute() {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()

  const cartReservations = useAppSelector(cartSlice.selectors.cartReservations)
  const mapRemoteData = useAppSelector(mapSelectors.mapRemoteData)
  const warehouse = useAppSelector(mapSlice.selectors.warehouse)
  const sectors = useAppSelector(mapSelectors.sectors)
  const isLoading = useAppSelector(mapSelectors.isLoading)
  const mappedSectors = useAppSelector(mappedSectorsSelector)
  const selectedElement = useAppSelector(mapSelectors.selectedElement)
  const license = useAppSelector(licenseSlice.selectors.license)
  const weatherInsurancePrice = useAppSelector(
    extraSlice.selectors.weatherInsurancePricePreview
  )
  const bookingAvailability = useAppSelector(
    bookingAvailabilitySlice.selectors.self
  )
  const hasPushedInsertMap = useRef(false)

  const { resetInsertInsuranceProducts } = useResetInsertInsuranceProducts()

  useFetchPricePreview()
  // Rebuild cartItems from restored cartReservations so the cart preview /
  // drawer stays in sync with the (persisted) total after re-entering the
  // sector flow, mirroring the map route behaviour.
  useSyncRestoredCartItems()

  useEffect(() => {
    if (
      isLoading ||
      hasPushedInsertMap.current ||
      !license ||
      !rd.isSuccess(weatherInsurancePrice)
    ) {
      return
    }
    const item = buildGaContextItem({
      bookingAvailability,
      license,
      weatherInsurable: weatherInsurancePrice.value > 0,
    })
    if (item) {
      pushEcommerceEvent('insert_map', {
        currency: GTM_CURRENCY_CODE,
        items: [item],
      })
    }
    hasPushedInsertMap.current = true
  }, [isLoading, license, weatherInsurancePrice, bookingAvailability])

  useEffect(() => {
    resetInsertInsuranceProducts()

    dispatch(
      loadMapData({
        cellSize: MAP_CELL_SIZE,
        screenHeight: window?.innerHeight ?? 0,
        screenWidth: window?.innerWidth ?? 0,
      })
    )
    dispatch(loadSectors())
    dispatch(
      servicesSlice.actions.load({
        purchaseType: ServiceInfoPurchaseType.WITH_SPOT,
      })
    )

    dispatch(mapSlice.actions.loadWarehouse())
  }, [])

  useEffect(() => {
    dispatch(cartSlice.actions.setBookingType(BookingAvailability.SPOTS))
  }, [])

  useEffect(() => {
    if (!rd.isSuccess(warehouse) || !rd.isSuccess(mapRemoteData)) {
      return
    }

    const elements = computeWarehouseAdjustedMapElements(store.getState())
    if (!elements) {
      return
    }

    dispatch(mapSlice.actions.updateMapElements(elements))
  }, [cartReservations, dispatch, mapRemoteData, sectors, warehouse])

  if (isLoading) {
    return <LoaderTrigger message={t('sectors.loading')} />
  }

  return (
    <>
      <main className="container max-w-5xl grow py-4 lg:py-8">
        <h1 className="mb-1 text-lg font-bold">{t('sectors.title')}</h1>
        <p className="mb-4">{t('sectors.subheading')}</p>

        <div className="mb-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {mappedSectors?.flatMap((sector) => {
            return (
              sector.elementsGroupedByType?.flatMap((group) => {
                const firstAvailableSpot = group.find(
                  (spot) =>
                    !cartReservations.find((reservation) =>
                      getObjectsComparison(
                        normalizeReservation(reservation),
                        normalizeSpot(spot)
                      )
                    )
                )

                const groupSpotsInCart = group.filter((spot) =>
                  cartReservations.find((reservation) =>
                    getObjectsComparison(
                      normalizeReservation(reservation),
                      normalizeSpot(spot)
                    )
                  )
                )

                const onAdd = () => {
                  if (!firstAvailableSpot) return
                  dispatch(
                    mapSlice.actions.selectMapElement(firstAvailableSpot)
                  )
                }

                const onRemove = () => {
                  dispatch(cartSlice.actions.setCartPopoverIsOpen(true))
                }

                return (
                  <SectorCard
                    availableQuantity={group.length - groupSpotsInCart.length}
                    imageUrl={sector.image}
                    key={sector.id + group[0].subType}
                    onAdd={onAdd}
                    onRemove={onRemove}
                    sectorName={sector.name}
                    selectedQuanitity={groupSpotsInCart.length}
                    spotType={group[0].subType as SpotType}
                  />
                )
              }) || []
            )
          })}
        </div>
        {selectedElement && <SelectedElementDrawer />}
        <SelectedServiceDrawer />
      </main>

      <CartFooter />
    </>
  )
}
